<div class="submission-confirmation-container">
  <div class="saving-overlay" *ngIf="isUpdating$ | async">
    <mat-spinner diameter="40"></mat-spinner>
    <span>Updating data...</span>
  </div>

  <div class="saving-overlay" *ngIf="pageLoading$ | async">
    <mat-spinner diameter="40"></mat-spinner>
    <span>Loading...</span>
  </div>

  <div class="confirmation-header">
    <h1>Submission Confirmation</h1>
    <p class="subtitle">
      Please review and confirm the following information before submitting to
      CMS.
    </p>
  </div>

  <ng-container *ngIf="reviewState$ | async as reviewState">
    <div class="alert alert-info already-submitted-banner" *ngIf="reviewState.isSubmitted && reviewState.status !== 'Submitted'">
      <mat-icon>check_circle</mat-icon>
      <div>
        <strong>Already Submitted</strong>
        <p>This submission has already been completed. You may go back to edit any category and resubmit if needed.</p>
      </div>
    </div>

    <div class="alert alert-info review-board-banner" *ngIf="reviewState.status === 'Review'">
      <mat-icon>hourglass_top</mat-icon>
      <div>
        <strong>Submitted for Review</strong>
        <p>Your submission has been submitted to REG-ENT for final review. If this was done in error, please cancel review.</p>
      </div>
    </div>

    <div class="alert alert-info review-board-banner" *ngIf="reviewState.status === 'Submitted'">
      <mat-icon>check_circle</mat-icon>
      <div>
        <strong>Submitted to CMS</strong>
        <p>Your submission has been completed and sent to CMS. Please see results page.</p>
      </div>
    </div>

    <div class="data-compare-bar" *ngIf="reviewState.isSuperUser && reviewState.submissionId && reviewState.isSubmitted">
      <span class="data-compare-label">
        <mat-icon>compare_arrows</mat-icon>
        Measure numerators, denominators, and rates: dashboard vs. CMS
      </span>
      <button
        type="button"
        class="btn btn-data-compare"
        (click)="onOpenSubmissionCheck(reviewState.submissionId)"
      >
        Compare Measure Data
      </button>
    </div>
  </ng-container>

  <div class="alert alert-warning" *ngIf="incompleteSteps.length > 0 && !isReadOnly">
    <div class="alert-header">
      <mat-icon>warning</mat-icon>
      <h3>Cannot Submit - Incomplete Steps</h3>
    </div>
    <p>You must complete the following steps before submitting:</p>
    <ul class="incomplete-steps-list">
      <li *ngFor="let step of incompleteSteps">
        <strong *ngIf="step.stepNumber !== submissionBlockStepNumber">Step {{ step.stepNumber }}: {{ step.stepName }}</strong>
        <strong *ngIf="step.stepNumber === submissionBlockStepNumber">{{ step.stepName }}: Blocked</strong>
        <span class="reason">{{ step.reason }}</span>
        <button
          *ngIf="step.field === 'mvpId'; else goToStepBtn"
          mat-stroked-button
          color="primary"
          (click)="refreshMvpRegistration()"
          class="go-to-step-btn"
        >
          Re-check MVP Registration
        </button>
        <ng-template #goToStepBtn>
          <button
            *ngIf="step.stepNumber !== submissionBlockStepNumber"
            mat-stroked-button
            color="primary"
            (click)="goToStep(step.stepNumber)"
            class="go-to-step-btn"
          >
            Go to Step
          </button>
        </ng-template>
      </li>
    </ul>
  </div>

  <div class="notice-banner info-banner compact">
    <div class="banner-icon">
      <i class="bi bi-info-circle"></i>
    </div>
    <div class="banner-content">
      <strong>Note:</strong> Beginning performance year 2023, CMS does not
      provide the composite and weighted category level preliminary scores.
    </div>
  </div>

  <div class="notice-banner warning-banner compact">
    <div class="banner-icon">
      <i class="bi bi-exclamation-triangle"></i>
    </div>
    <div class="banner-content">
      <strong>Notice:</strong> The achievement points and category weights
      displayed on the dashboard are estimated and may differ from the actual
      MIPS composite score achieved after submitting data to CMS. The Cost
      category score is computed by CMS and will be added in your MIPS final
      score (if applicable).
    </div>
  </div>

  <div
    class="notice-banner third-party-banner"
    *ngIf="thirdPartyCategories.length > 0"
  >
    <div class="banner-icon">
      <mat-icon>info</mat-icon>
    </div>
    <div class="banner-content">
      <strong>Third-Party Intermediary:</strong>
      The following
      {{ thirdPartyCategories.length === 1 ? "category is" : "categories are" }}
      being submitted via a third-party intermediary and
      {{ thirdPartyCategories.length === 1 ? "is" : "are" }} not included in the
      scores below: <strong>{{ thirdPartyCategories.join(", ") }}</strong
      >.
    </div>
  </div>

  <div class="confirmation-section">
    <h2>Selected Categories to Submit</h2>

    <div class="reporting-tabs" *ngIf="isMultiPacketMode">
      <button
        class="tab-btn"
        [class.active]="currentTab === 'traditional'"
        (click)="onTabChange('traditional')"
      >
        Traditional MIPS
      </button>
      <button
        class="tab-btn"
        [class.active]="currentTab === 'mvp'"
        (click)="onTabChange('mvp')"
      >
        MVP
      </button>
    </div>

    <div
      class="notice-banner info-banner"
      *ngIf="selectedCategories.length < 3 && !hasThirdPartyExclusions"
    >
      <div class="banner-icon">
        <mat-icon>info</mat-icon>
      </div>
      <div class="banner-content">
        <strong>Note:</strong> You are submitting
        {{ selectedCategories.length }} of 3 categories.
        <span *ngIf="!hasQuality">
          Quality category is not being submitted.</span
        >
        <span *ngIf="!hasPI">
          Promoting Interoperability category is not being submitted.</span
        >
        <span *ngIf="!hasIA">
          Improvement Activities category is not being submitted.</span
        >
        Category weights will be adjusted accordingly.
      </div>
    </div>

    <div class="categories-table">
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Estimated Score / Max</th>
            <th>Weight (%)</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let category of selectedCategories">
            <td>
              {{ category.name }}
              <span
                class="shared-indicator"
                *ngIf="category.key === 'pi' && isMultiPacketMode"
              >
                (shared)
              </span>
            </td>
            <td>
              {{ category.estimatedScore | number: "1.0-1" }}
              <span class="score-separator">/</span>
              <span class="score-max">{{ category.maxScore }}</span>
            </td>
            <td>
              {{ category.estimatedWeight | number }}%
              <span class="weight-separator">/</span>
              <span class="weight-max">{{ category.maxWeight }}%</span>
              <mat-icon
                *ngIf="category.maxWeight === 0"
                class="reweighted-icon"
                matTooltip="This category has been reweighted to 0% based on your eligibility and reporting selections"
                >info_outline</mat-icon
              >
            </td>
          </tr>
          <tr *ngIf="selectedCategories.length === 0">
            <td colspan="3" class="no-data">
              <span *ngIf="hasThirdPartyExclusions; else noCategories">
                All categories are being submitted via a third-party
                intermediary.
              </span>
              <ng-template #noCategories>No categories selected</ng-template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="email-inline">
    <mat-icon class="email-icon">email</mat-icon>
    <span class="email-label">Feedback Email:</span>
    <span class="email-value">{{ registeredEmail || "Not provided" }}</span>
  </div>

  @if (!isReadOnly) {
  <div class="confirmation-section">
    <h2>Attestations</h2>
    <form [formGroup]="confirmationForm" class="attestations-form">
      <div class="form-check">
        <input
          type="checkbox"
          id="tinVerification"
          formControlName="tinVerification"
          class="form-check-input"
          [class.is-invalid]="
            tinVerification?.invalid && tinVerification?.touched
          "
        />
        <label for="tinVerification" class="form-check-label">
          I have verified that my TIN <strong>{{ tin }}</strong> is accurate.
          <span class="required">*</span>
        </label>
        <div
          class="invalid-feedback"
          *ngIf="tinVerification?.invalid && tinVerification?.touched"
        >
          This attestation is required
        </div>
      </div>

      <div class="form-check">
        <input
          type="checkbox"
          id="drcfAttestation"
          formControlName="drcfAttestation"
          class="form-check-input"
          [class.is-invalid]="
            drcfAttestation?.invalid && drcfAttestation?.touched
          "
        />
        <label for="drcfAttestation" class="form-check-label">
          I have read, reviewed and signed/attested to the Data Release Consent
          Form.
          <span class="required">*</span>
        </label>
        <div
          class="invalid-feedback"
          *ngIf="drcfAttestation?.invalid && drcfAttestation?.touched"
        >
          This attestation is required
        </div>
      </div>

      <div class="form-check">
        <input
          type="checkbox"
          id="dataAccuracy"
          formControlName="dataAccuracy"
          class="form-check-input"
          [class.is-invalid]="dataAccuracy?.invalid && dataAccuracy?.touched"
        />
        <label for="dataAccuracy" class="form-check-label">
          I have verified that the data I am reporting is true and accurate to
          the best of my knowledge.
          <span class="required">*</span>
        </label>
        <div
          class="invalid-feedback"
          *ngIf="dataAccuracy?.invalid && dataAccuracy?.touched"
        >
          This attestation is required
        </div>
      </div>
    </form>
  </div>
  } <!-- end @if (!isReadOnly) -->

<div class="action-buttons" *ngIf="!isReadOnly">
  <ng-container *ngIf="reviewState$ | async as reviewState">
    <button
      type="button"
      class="btn btn-secondary"
      (click)="onCancel()"
      [disabled]="(isSubmitting$ | async) || false"
    >
      Cancel
    </button>

    <button
      type="button"
      class="btn btn-review-ready"
      *ngIf="reviewState.hasAdminRights && reviewState.status !== 'Review' && reviewState.status !== 'Submitted'"
      (click)="onReadyToSubmit(reviewState.submissionId!)"
      [disabled]="
        !canSubmit ||
        confirmationForm.invalid ||
        (isSubmitting$ | async) ||
        !reviewState.submissionId
      "
    >
      Ready to Submit
    </button>

    <button
      type="button"
      class="btn btn-review-cancel"
      *ngIf="reviewState.hasAdminRights && reviewState.status === 'Review'"
      (click)="onCancelReview(reviewState.submissionId!)"
      [disabled]="!reviewState.submissionId"
    >
      Cancel Review
    </button>

    <button
      type="button"
      class="btn btn-primary"
      (click)="onSubmit()"
      [disabled]="
        !canSubmit ||
        confirmationForm.invalid ||
        (isSubmitting$ | async) ||
        isReadOnly ||
        (reviewState.status === 'Review' && !reviewState.isSuperUser)
      "
    >
      <span *ngIf="!(isSubmitting$ | async)">{{ reviewState.isSubmitted ? 'Resubmit to CMS' : 'Submit to CMS' }}</span>
      <span *ngIf="isSubmitting$ | async">
        <span class="spinner-border spinner-border-sm me-2"></span>
        Submitting...
      </span>
    </button>
  </ng-container>
</div>
</div>
